import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Chip,
  Autocomplete,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  LockReset as LockResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { AgronodTypography } from "@agronod/mui-components";
import { AdminService } from "@/services/admin.service";
import { UserRole, UpdateUserPayload, AdminUserDetails } from "@/types";
import { enqueueSnackbar } from "notistack";

// Konstanter (samma som i Create)
const SWEDISH_REGIONS = [
  "Blekinge",
  "Dalarna",
  "Gotland",
  "Gävleborg",
  "Halland",
  "Jämtland",
  "Jönköping",
  "Kalmar",
  "Kronoberg",
  "Norrbotten",
  "Skåne",
  "Stockholm",
  "Södermanland",
  "Uppsala",
  "Värmland",
  "Västerbotten",
  "Västernorrland",
  "Västmanland",
  "Västra Götaland",
  "Örebro",
  "Östergötland",
];

const PRODUCTION_TYPES = [
  "Ekologisk",
  "Konventionell",
  "Friland",
  "Växthus",
  "Djurhållning",
  "Växtodling",
];

const CERTIFICATIONS = [
  "KRAV",
  "EU-Eko",
  "Demeter",
  "Sigill",
  "Svenskt Sigill",
  "MSC",
  "ASC",
];

const AdminEditUserPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // State
  const [user, setUser] = useState<AdminUserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [formData, setFormData] = useState<UpdateUserPayload>({});

  // Password reset dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load user details
  useEffect(() => {
    const loadUser = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const userData = await AdminService.getUserDetails(id);
        setUser(userData);

        // Initialize form data
        setFormData({
          email: userData.email,
          role: userData.role,
          isActive: userData.isActive,
          farmName: userData.farmProfile.farmName,
          organizationNumber: userData.farmProfile.organizationNumber || "",
          region: userData.farmProfile.region,
          productionTypes: userData.farmProfile.productionTypes,
          certifications: userData.farmProfile.certifications,
        });
      } catch (err: any) {
        console.error("Failed to load user:", err);
        setError(err.message || "Kunde inte ladda användare");
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [id]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors["email"] = "Ogiltig e-postadress";
    }

    if (formData.farmName && formData.farmName.length < 2) {
      newErrors["farmName"] = "Gårdsnamnet måste vara minst 2 tecken långt";
    }

    if (
      formData.organizationNumber &&
      formData.organizationNumber.length > 0 &&
      !/^\d{6}-\d{4}$/.test(formData.organizationNumber)
    ) {
      newErrors["organizationNumber"] =
        "Organisationsnummer måste ha formatet XXXXXX-XXXX";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validate() || !id) {
      return;
    }

    setSaving(true);

    try {
      await AdminService.updateUser(id, formData);
      enqueueSnackbar("Användare uppdaterad!", { variant: "success" });
      navigate("/admin/users");
    } catch (err: any) {
      console.error("Failed to update user:", err);
      setError(err.message || "Kunde inte uppdatera användare. Försök igen.");
    } finally {
      setSaving(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async () => {
    if (!id || !newPassword) return;

    if (newPassword.length < 8) {
      enqueueSnackbar("Lösenordet måste vara minst 8 tecken långt", {
        variant: "error",
      });
      return;
    }

    setResetting(true);

    try {
      await AdminService.resetPassword(id, newPassword);
      enqueueSnackbar(
        "Lösenord återställt! Meddela användaren det nya lösenordet.",
        { variant: "success" }
      );
      setResetDialogOpen(false);
      setNewPassword("");
    } catch (err: any) {
      console.error("Failed to reset password:", err);
      enqueueSnackbar(err.message || "Kunde inte återställa lösenord", {
        variant: "error",
      });
    } finally {
      setResetting(false);
    }
  };

  // Copy password to clipboard
  const handleCopyPassword = () => {
    navigator.clipboard.writeText(newPassword);
    enqueueSnackbar("Lösenord kopierat!", { variant: "success" });
  };

  if (loading) {
    return (
      <Container
        maxWidth="md"
        sx={{ py: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error && !user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/users")}
          sx={{ mt: 2 }}
        >
          Tillbaka till användare
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/users")}
          sx={{ mb: 2 }}
        >
          Tillbaka till användare
        </Button>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <AgronodTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Redigera användare
            </AgronodTypography>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {user?.email}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<LockResetIcon />}
            onClick={() => setResetDialogOpen(true)}
            color="warning"
          >
            Återställ lösenord
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{ p: 4, border: "1px solid", borderColor: "divider" }}
      >
        <form onSubmit={handleSubmit}>
          {/* Error alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* ANVÄNDARKONTO */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Användarkonto
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
            <TextField
              fullWidth
              label="E-postadress"
              type="email"
              value={formData.email || ""}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              error={Boolean(errors["email"])}
              helperText={errors["email"]}
            />

            <FormControl fullWidth>
              <InputLabel>Roll</InputLabel>
              <Select
                value={formData.role || UserRole.AGROKOLL_OWNER}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value as UserRole })
                }
                label="Roll"
              >
                <MenuItem value={UserRole.AGROKOLL_OWNER}>
                  ÄGARE - Kontoägare
                </MenuItem>
                <MenuItem value={UserRole.AGROKOLL_ADMIN}>
                  ADMIN - Kontoadministratör
                </MenuItem>
                <MenuItem value={UserRole.AGROKOLL_SYSTEM_ADMIN}>
                  CONTROL_ADMIN - Kan hantera kontrolltyper
                </MenuItem>
                <MenuItem value={UserRole.AGROKOLL_SYSTEM_ADMIN}>
                  SYSTEM_ADMIN - Full systemadministration
                </MenuItem>
                <MenuItem value={UserRole.AGROKOLL_SYSTEM_ADMIN}>
                  SUPER_ADMIN - Allt + användarhantering
                </MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive ?? true}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label="Aktiv användare"
            />
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* GÅRDSPROFIL */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Gårdsprofil
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Gårdsnamn"
              value={formData.farmName || ""}
              onChange={(e) =>
                setFormData({ ...formData, farmName: e.target.value })
              }
              error={Boolean(errors["farmName"])}
              helperText={errors["farmName"]}
            />

            <TextField
              fullWidth
              label="Organisationsnummer"
              value={formData.organizationNumber || ""}
              onChange={(e) =>
                setFormData({ ...formData, organizationNumber: e.target.value })
              }
              error={Boolean(errors["organizationNumber"])}
              helperText={
                errors["organizationNumber"] || "Format: XXXXXX-XXXX (valfritt)"
              }
              placeholder="123456-7890"
            />

            <Autocomplete
              fullWidth
              options={SWEDISH_REGIONS}
              value={formData.region || ""}
              onChange={(_, value) =>
                setFormData({ ...formData, region: value || "" })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Region"
                  error={Boolean(errors["region"])}
                  helperText={errors["region"]}
                />
              )}
            />

            <Autocomplete
              multiple
              fullWidth
              options={PRODUCTION_TYPES}
              value={formData.productionTypes || []}
              onChange={(_, value) =>
                setFormData({ ...formData, productionTypes: value })
              }
              renderValue={(value, getItemProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getItemProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Produktionstyper"
                  placeholder="Välj eller skriv..."
                />
              )}
              freeSolo
            />

            <Autocomplete
              multiple
              fullWidth
              options={CERTIFICATIONS}
              value={formData.certifications || []}
              onChange={(_, value) =>
                setFormData({ ...formData, certifications: value })
              }
              renderValue={(value, getItemProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getItemProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Certifieringar"
                  placeholder="Välj eller skriv..."
                />
              )}
              freeSolo
            />
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              fullWidth
            >
              {saving ? "Sparar..." : "Spara ändringar"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/admin/users")}
              disabled={saving}
              fullWidth
            >
              Avbryt
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Password Reset Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Återställ lösenord</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Användarens nuvarande lösenord kommer att ersättas. Se till att
            meddela användaren det nya lösenordet säkert.
          </Alert>
          <TextField
            fullWidth
            label="Nytt lösenord"
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Minst 8 tecken"
            autoFocus
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                    {newPassword && (
                      <IconButton onClick={handleCopyPassword} edge="end">
                        <CopyIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResetDialogOpen(false)}
            disabled={resetting}
          >
            Avbryt
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="warning"
            disabled={resetting || !newPassword}
          >
            {resetting ? "Återställer..." : "Återställ lösenord"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminEditUserPage;
